import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash, FileText, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { money, formatApiErrorDetail } from "../../lib/api";
import { SectionHeader, Money, useFocusHighlight, NotesBadge } from "../../components/shared";
import { CsvTools } from "../../components/CsvTools";
import { DataTable } from "../../components/DataTable";
import { PARTY_EXTRA } from "../../lib/extraColumns";
import { Button } from "../../components/ui/button";
import { usePerms } from "../../lib/perms";
import { SuppliersBySpend } from "../../components/SuppliersBySpend";

export function Vendors() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { rowProps } = useFocusHighlight();
  const { can } = usePerms();

  const { data: vendors = [] } = useQuery({ queryKey: ["vendors"], queryFn: async () => (await api.get("/vendors")).data });

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/vendors/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vendors"] }); toast.success("Vendor deleted"); },
  });
  const emailStatement = useMutation({
    mutationFn: async (id) => (await api.post(`/vendors/${id}/statement-email`)).data,
    onSuccess: (res) => toast.success(`Statement (${money(res.total)}) emailed to ${res.sent_to}`),
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <>
      <SuppliersBySpend />
      <SectionHeader title="Vendors" subtitle="Accounts payable">
        <div className="flex items-center gap-2 flex-wrap">
          <CsvTools
            entity="vendors"
            queryKey={["vendors"]}
            label="vendors"
            rows={vendors}
            columns={[
              { key: "code", label: "code", importKey: false },
              { key: "name", label: "name" },
              { key: "email", label: "email" },
              { key: "phone", label: "phone" },
              { key: "address", label: "address" },
              { key: "balance", label: "balance", importKey: false },
            ]}
          />
          {can("vendors.edit") && <Button onClick={() => navigate("/purchasing/vendor/new")} data-testid="new-vendor-button" className="rounded-sm bg-primary hover:bg-primary/90 h-9"><Plus size={16} weight="bold" className="mr-1" /> New Vendor</Button>}
        </div>
      </SectionHeader>
      <DataTable
        testid="vendors-table"
        data={vendors}
        rowKey={(v) => v.id}
        rowTestid="vendor-row"
        rowProps={(v) => rowProps(v.id)}
        empty="No vendors yet."
        viewKey="vendors" namedViews extraColumns={PARTY_EXTRA}
        columns={[
          { key: "code", header: "Code", filter: "text", cellClassName: "font-mono text-xs", cell: (v) => <button onClick={() => navigate(`/purchasing/vendor/${v.id}`)} data-testid="open-vendor" className="text-primary hover:underline">{v.code}</button> },
          { key: "name", header: "Name", filter: "text", cellClassName: "text-sm font-medium", cell: (v) => <button onClick={() => navigate(`/purchasing/vendor/${v.id}`)} className="text-left hover:underline inline-flex items-center gap-1.5">{v.name}<NotesBadge count={v.notes?.length} testid="vendor-notes-count" /></button> },
          { key: "email", header: "Contact", filter: "text", cellClassName: "text-sm text-muted-foreground" },
          { key: "currency", header: "Currency", filter: "text", cellClassName: "font-mono text-xs", cell: (v) => v.currency || "AUD" },
          { key: "balance", header: "Balance", align: "right", numeric: true, cell: (v) => <Money value={v.balance} className="text-sm font-medium" /> },
          { key: "actions", header: "", sortable: false, align: "right", cell: (v) => (
            <div className="flex justify-end gap-1">
              {can("vendors.edit") && <button onClick={() => emailStatement.mutate(v.id)} disabled={emailStatement.isPending} className="p-1.5 text-muted-foreground hover:text-primary disabled:opacity-40" data-testid="email-vendor-statement" title="Email aged-payables statement"><EnvelopeSimple size={16} weight="bold" /></button>}
              <button onClick={() => navigate(`/purchasing/vendor/${v.id}`)} className="p-1.5 text-muted-foreground hover:text-primary" data-testid="edit-vendor"><FileText size={16} /></button>
              {can("purchasing.delete") && <button onClick={() => del.mutate(v.id)} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid="delete-vendor"><Trash size={16} /></button>}
            </div>
          ) },
        ]}
      />
    </>
  );
}
